import {
  type CSSProperties,
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { tripStart } from "../../src/days.ts";
import { hasDay } from "../../src/plan-day.ts";
import type { Tier } from "../../src/tiers.ts";
import { dueSoonFor } from "../../src/waits.ts";
import { TIPS } from "../../src/web/types.ts";
import { AskAi } from "./AskAi.tsx";
import { askedOf } from "./Asks.tsx";
import {
  ApiError,
  type Ask,
  api,
  type Change,
  deletedTrip,
  type Item,
  type Link,
  type Me,
  type Note,
  type Pad,
  type PadEntry,
  type Reminder,
  type Research,
  type SharedView,
  type TravelDocument,
  type TripFile,
  type TripView,
  type TripWeather,
  type WeatherDay,
} from "./api.ts";
import {
  type AddView,
  type CardEdit,
  cardAsks,
  cardEdits,
  examplePrompt,
  plannable,
  type ViewEdit,
  viewAsks,
  viewEdits,
} from "./ask-ai.ts";
import { BookingForm, type Saved } from "./BookingForm.tsx";
import { Bookings } from "./Bookings.tsx";
import { liveText } from "./changes.ts";
import { CaughtView } from "./crash.tsx";
import { Days } from "./Days.tsx";
import { Deck, useDeck } from "./Deck.tsx";
import { CloseCard, Details, Peek, ShowOnMap } from "./Details.tsx";
import { type DeckRun, deckShows } from "./deck.ts";
import { early, tripKey } from "./early.ts";
import { failureOf } from "./failure.ts";
import { YourCurrency } from "./fields.tsx";
import { ago, KINDS, type Kind, kindOf, localToday, myTier, recommenders, waitingSuggestion } from "./format.ts";
import { membersNow } from "./former.tsx";
import { distanceM, type LngLat, onMapTypes, point } from "./geo.ts";
import { useHeight, useLoad, useMedia, useTheme } from "./hooks.ts";
import { Hovered, useHoverCard } from "./hover.ts";
import { Ideas } from "./Ideas.tsx";
import { InviteCard } from "./Invite.tsx";
import { inviteDue } from "./inviting.ts";
import { type Done, JustDoneProvider } from "./justDone.ts";
import { type Focus, markedIn, markOpened, useKeptScroll } from "./kept.ts";
import { linkRows } from "./links.ts";
import { useLive } from "./live.ts";
import { MapFilters } from "./MapFilters.tsx";
import { Overview } from "./MapList.tsx";
import { Modal } from "./Modal.tsx";
import { DIMMED, NEARBY_M, type Padding } from "./map/common.ts";
import { TripMap } from "./map/load.tsx";
import { FOLD_PAST, found, listLayout, SEARCH_PAUSE_MS, searchText } from "./maplist.ts";
import { NewVersion } from "./NewVersion.tsx";
import { NoSignal } from "./NoSignal.tsx";
import { useAgain, useKept, useNewVersion, useOnline } from "./offline.ts";
import { LastOpened } from "./opened.ts";
import { Failed, Opening } from "./opening.tsx";
import { People } from "./People.tsx";
import { OwnPhotos } from "./Photos.tsx";
import { choiceOf, choices, inPlan, isOpenOption, isOptionTile, openOptions, ratingWaits } from "./plan.ts";
import { type Finding, ResearchReader } from "./Research.tsx";
import { ReadOnly, useReadOnly } from "./readOnly.ts";
import { keepFilters, keptFilters, markShown, reopen } from "./reopen.ts";
import { navigate, type Route, type StepState, stepState, tripPath } from "./router.ts";
import { ShareItem } from "./ShareItem.tsx";
import { LockedNotice } from "./Spots.tsx";
import { AvatarStack, Button, Chip, Icon, type IconName } from "./souvenir.ts";
import { FirstVisit, TipsProvider, viewPrompt } from "./Tips.tsx";
import { Today, TodayButton, useTodayWindow } from "./Today.tsx";
import { type Removed, TodoEditsProvider, UndoNotice, useTodoEdits } from "./Todos.tsx";
import { TripGone } from "./TripGone.tsx";
import { TripMenu } from "./TripMenu.tsx";
import { TripSettings } from "./TripSettings.tsx";
import { Updates } from "./Updates.tsx";
import { BackToTrips, Badge, Empty, Message, Wordmark } from "./ui.tsx";
import { StaticTripName, ViewBar } from "./ViewBar.tsx";
import { ViewersAsk } from "./ViewersAsk.tsx";
import { useViewing } from "./viewing.ts";
import { useShareWhereIAm, useWhereIAm } from "./where.ts";

type View = "map" | "days" | "ideas" | "people" | "bookings" | "updates";

// On a computer, what stands at the map's foot (the small card, the notices) stands this far up from its
// bottom edge (px): level with the foot of the zoom buttons, clear of the map's credit under them.
const FOOT = 54;
// How tall the small card is on a computer (px), until it's measured. On a phone, how much taller what
// floats above the tabs is with a place's small card in it than with the list button, until it's seen.
const BRIEF_HEIGHT = 86;
const BRIEF_MORE = 42;

const VIEWS: { key: View; label: string; icon: IconName }[] = [
  { key: "map", label: "Map", icon: "map" },
  { key: "days", label: "Days", icon: "calendar" },
  { key: "ideas", label: "Ideas", icon: "star" },
  { key: "people", label: "People", icon: "people" },
  { key: "bookings", label: "Bookings", icon: "lock" },
  { key: "updates", label: "Updates", icon: "bell" },
];
// A view as what says it broke names it (crash.tsx); none is Trip settings, open over the view.
const viewWords = (view: View | null) =>
  view === null ? "Trip settings" : view === "map" ? "the map" : (VIEWS.find((v) => v.key === view)?.label ?? view);

export function TripPage({
  me,
  trip,
  item,
  view: viewName,
  attach,
  list,
  full,
  settings,
  rate,
  today,
  reading,
  about,
}: {
  me: Me;
  trip: string;
  item: string | null;
  view: string | null;
  // From an AI's upload link: "1" for the open item ("photo" to add a photo of it), or a document's id.
  attach: string | null;
  // On a phone, the list over the map; on a phone and a computer, the open item's card in full.
  list: boolean;
  full: boolean;
  // Trip settings, open.
  settings: boolean;
  // The deck, rating what waits on me one at a time (Deck.tsx).
  rate: boolean;
  // Today, the day from my side, over the view (Today.tsx).
  today: boolean;
  // A finding open to be read in full over the view (`?view=ideas&research=<id>`; Research.tsx), and a card
  // opened from it, over it (`about`).
  reading: string | null;
  about: string | null;
}) {
  // Opened on its bare address, the trip is where it was when I left it, before anything shows; and
  // every step it shows from then on is where it is.
  const user = me.user.id;
  useLayoutEffect(() => reopen(user, trip), [user, trip]);
  useEffect(() => markShown());
  // Bumped after a change made here (letting someone in), to load the trip again.
  const [version, setVersion] = useState(0);
  // Back online (or once this phone keeps it): load it again.
  const again = useAgain();
  // The first time, as loaded alongside who I am, when the scripts started (main.tsx).
  const data = useLoad(
    () => early(tripKey(trip), () => Promise.all([api.trip(trip), api.items(trip)])),
    [trip, version, again],
  );
  // Each day's weather, on its own: it may take a moment to look up, and the plan never waits for it.
  // Loaded with the trip, so the phone keeps it for Days without signal.
  const weather = useLoad(() => api.weather(trip), [trip, version, again]);
  // Until it's in, the sunrise (features/opening.md), over the trip's own paper.
  if (data.state === "loading")
    return (
      <div className="tm-trip-page" aria-busy="true">
        <Opening />
      </div>
    );
  if (data.state === "failed") {
    // Its owner deleted it: who and when (and, to its owner, Restore).
    const gone = deletedTrip(data.error);
    if (gone) return <TripGone trip={gone} you={me.user.id} onRestored={() => setVersion((v) => v + 1)} />;
    // Not in it (or there's no such trip: Tripmo doesn't say which), or not let in yet: a page that says so,
    // with nothing of the trip or what the link was to, and never a way in (features/map.md, Share).
    const refused = notIn(data.error, item !== null);
    if (refused) return <NotInTrip {...refused} />;
    // No signal and nothing kept, or Tripmo not answering: said plainly, with Try again.
    const failure = failureOf(data.error);
    if (failure)
      return (
        <Failed
          failure={failure}
          what="trip"
          again={() => setVersion((v) => v + 1)}
          retrying={data.retrying === true}
          back={<BackToTrips />}
        />
      );
    return <Message title="Can't open this trip" body={data.message} action={<BackToTrips />} />;
  }
  const [view, { items, links, pads, entries, notes, asks, reminders, documents, research, files }] = data.data;
  // A finding open to be read, over the view (or the map, or the deck) as it was; Trip settings is over the
  // view instead.
  const read = settings ? null : reading;
  return (
    <OwnPhotos files={files}>
      <TipsProvider trip={view.title} seen={me.tips_seen}>
        <Trip
          me={me}
          trip={view}
          items={items}
          links={links}
          pads={pads}
          entries={entries}
          notes={notes}
          asks={asks}
          reminders={reminders}
          documents={documents}
          research={research}
          files={files}
          weather={weather.state === "ready" ? weather.data.days : null}
          mornings={weather.state === "ready" ? (weather.data.mornings ?? []) : null}
          attach={attach}
          list={list}
          full={full}
          settings={settings}
          // A read-only trip takes no ratings: no deck.
          rate={rate && !settings && !view.locked}
          today={today && !settings && !(rate && !view.locked)}
          selected={item}
          reading={read}
          readerItem={read ? about : null}
          // "options" is the Ideas tab's old address.
          view={VIEWS.find((v) => v.key === (viewName === "options" ? "ideas" : viewName))?.key ?? "map"}
          onChanged={() => setVersion((v) => v + 1)}
        />
      </TipsProvider>
    </OwnPhotos>
  );
}

// A shared trip opened by its view link (features/sharing.md; ViewPage.tsx): the trip's own views over what the view's
// API gave (the trip as a viewer sees it, nothing of what isn't shared), read-only, at the link's own steps.
export function ViewedTrip({
  me,
  trip,
  shared,
  weather,
  route,
  onChanged,
}: {
  me: Me;
  trip: TripView;
  shared: Pick<SharedView, "items" | "links" | "research" | "notes" | "files" | "documents">;
  weather: TripWeather | null;
  route: Extract<Route, { name: "view" }>;
  onChanged: () => void;
}) {
  const viewing = useViewing();
  const shown = viewing
    ? VIEWS.filter((v) => v.key !== "updates" && (v.key !== "people" || viewing.shares.has("names")))
    : VIEWS;
  return (
    <OwnPhotos files={shared.files}>
      <TipsProvider trip={trip.title} seen={[...TIPS]}>
        <Trip
          me={me}
          trip={trip}
          items={shared.items}
          links={shared.links}
          pads={[]}
          entries={[]}
          notes={shared.notes}
          asks={[]}
          reminders={[]}
          documents={shared.documents}
          research={shared.research}
          files={shared.files}
          weather={weather?.days ?? null}
          mornings={weather?.mornings ?? null}
          attach={null}
          list={route.list}
          full={route.full}
          settings={false}
          rate={false}
          today={false}
          selected={route.item}
          reading={route.research}
          readerItem={route.research ? route.about : null}
          view={shown.find((v) => v.key === route.view)?.key ?? "map"}
          onChanged={onChanged}
        />
      </TipsProvider>
    </OwnPhotos>
  );
}

type TripProps = Omit<Parameters<typeof TripBody>[0], "justDone" | "setJustDone">;

// The trip as loaded, with the to-do edits I've made and it hasn't loaded yet on it.
function Trip(props: TripProps) {
  const { items, edits } = useTodoEdits(props.trip.id, props.items, props.onChanged);
  // What I just did on the web (a to-do removed, a booking saved, a day planned, a question answered): Undo,
  // offered at once where the notices go, by whatever did it (justDone.ts).
  const [justDone, setJustDone] = useState<Done | null>(null);
  // A shared trip's view (viewing.ts): read-only as a locked trip is, and more (TripBody).
  const viewing = useViewing();
  return (
    <ReadOnly.Provider value={Boolean(props.trip.locked) || viewing !== null}>
      <TodoEditsProvider value={edits}>
        <JustDoneProvider value={setJustDone}>
          <YourCurrency.Provider value={props.trip.your_currency?.currency ?? props.me.currency ?? null}>
            <TripBody {...props} items={items} justDone={justDone} setJustDone={setJustDone} />
          </YourCurrency.Provider>
        </JustDoneProvider>
      </TodoEditsProvider>
    </ReadOnly.Provider>
  );
}

function TripBody({
  me,
  trip,
  items,
  links,
  pads,
  entries,
  notes,
  asks,
  reminders,
  documents,
  research,
  files,
  weather,
  mornings,
  attach,
  list,
  full,
  settings,
  rate,
  today,
  selected,
  reading,
  readerItem,
  view,
  onChanged,
  justDone,
  setJustDone,
}: {
  // What I just did, and saying it (Trip, above).
  justDone: Done | null;
  setJustDone: Dispatch<SetStateAction<Done | null>>;
  me: Me;
  trip: TripView;
  items: Item[];
  links: Link[];
  pads: Pad[];
  entries: PadEntry[];
  notes: Note[];
  asks: Ask[];
  reminders: Reminder[];
  documents: TravelDocument[];
  research: Research[];
  files: TripFile[];
  weather: WeatherDay[] | null;
  // On a moving day, the weather where we leave that morning (Today).
  mornings: WeatherDay[] | null;
  attach: string | null;
  list: boolean;
  full: boolean;
  settings: boolean;
  // The deck is open (`?view=ideas&rate=1`): on a computer in the panel at the map's right, the place on
  // the map beside it; on a phone, a sheet over the view. What's opened from it opens over it.
  rate: boolean;
  // Today over the view (`?today=1`), from the day before the trip to the day after; with `selected`, a card
  // opened from it over it.
  today: boolean;
  selected: string | null;
  // A finding open to be read over the view, and a card opened from it, over it (TripPage); `selected`
  // is what's open under them.
  reading: string | null;
  readerItem: string | null;
  view: View;
  onChanged: () => void;
}) {
  const wide = useMedia("(min-width: 900px)");
  const theme = useTheme();
  // On a shared trip's view link (features/sharing.md): its views but Updates (and People only with names shared),
  // no Today, Add, Change with your AI, Share, rating or Trip settings, and nothing heard live.
  const viewing = useViewing();
  const views = viewing
    ? VIEWS.filter((v) => v.key !== "updates" && (v.key !== "people" || viewing.shares.has("names")))
    : VIEWS;
  // The map's filters, as the trip was left on this device.
  const [filtering, setFilters] = useFilters(me.user.id, trip, items);
  const { person, yesOnly, friend, kinds } = filtering;
  const setPerson = (person: string) => setFilters((f) => ({ ...f, person }));
  const setYesOnly = (yesOnly: boolean) => setFilters((f) => ({ ...f, yesOnly }));
  const setFriend = (friend: string | null) => setFilters((f) => ({ ...f, friend }));
  const setKinds = (kinds: Kind[]) => setFilters((f) => ({ ...f, kinds }));
  // The invitation to bring the others in, put away here (Tripmo keeps it so for every device).
  const [invitePutAway, setInvitePutAway] = useState(false);
  // The open card's booking form (BookingForm.tsx), over the card: in its panel on a computer, a sheet over it on
  // a phone. The id of what it's for, and whether it opens at its Not booked after all (from Change with your AI).
  const [booking, setBooking] = useState<string | null>(null);
  const [bookingAt, setBookingAt] = useState<"unbook" | null>(null);
  // Change with your AI (AskAi.tsx): a change chosen on a small card, started once its card is open in full;
  // and one chosen from a view's Add that the view itself does (saving a link on Ideas, planning a day on Days).
  const [startEdit, setStartEdit] = useState<{ id: string; edit: CardEdit } | null>(null);
  const [viewEdit, setViewEdit] = useState<ViewEdit | null>(null);
  // On a computer, what's framed on the map keeps clear of the notices and the small card at its foot.
  const [noticesRef, noticesHeight] = useHeight();
  const [briefRef, briefHeight] = useHeight();

  // The trip names the tab, and Trip settings over it its own (set here: a view's effect runs before
  // the page's, so the trip's name would win on a fresh load, and closing settings wouldn't put it back).
  useEffect(() => {
    document.title = settings
      ? `Trip settings · ${trip.title} · Tripmo`
      : viewing
        ? `${trip.title} · ${viewing.sample ? "A sample trip" : "View only"} · Tripmo`
        : `${trip.title} · Tripmo`;
  }, [trip.title, settings, viewing]);

  // Worked out once per load of the trip, not on every keystroke of a search.
  const planned = useMemo(() => inPlan(items), [items]);
  // Cancelled bookings are off the map (Bookings still lists them).
  // And every choice's stay and place options still open, with a place, as options (features/map.md),
  // booked one or not: each option's choice.
  const optionsOf = useMemo(() => openOptions(items), [items]);
  const visible = useMemo(
    () => [
      ...planned.filter((i) => onMapTypes.has(i.type) && i.status !== "cancelled"),
      ...items.filter((i) => optionsOf.has(i.id)),
    ],
    [planned, items, optionsOf],
  );
  // How many of them are where the map can show them.
  const placed = visible.filter((i) => point(i) !== null).length;
  // The list's search, narrowing it and dimming the rest of the map (features/map.md): what each item is
  // searched by, and how what's on the map is grouped, from everything on it. Not kept when the trip reopens.
  const [search, setSearch] = useState("");
  const texts = useMemo(() => new Map(visible.map((i) => [i.id, searchText(i)])), [visible]);
  const hits = useMemo(() => found(texts, search), [texts, search]);
  const layout = useMemo(
    () =>
      listLayout(
        visible.filter((i) => point(i) !== null),
        tripStart(trip.dates),
        optionsOf,
      ),
    [visible, trip.dates, optionsOf],
  );
  // Each time typing pauses on a search, the map is asked to show its matches: `n` counts the asks.
  const [frameAsk, setFrameAsk] = useState<{ n: number; query: string } | null>(null);
  const pause = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(pause.current), []);
  // As typed: the list and the map follow in a transition, so typing never waits for them.
  const onSearch = useCallback((q: string) => {
    startTransition(() => setSearch(q));
    // Once typing pauses, the map shows the matches (never between keystrokes, nor on clearing).
    clearTimeout(pause.current);
    if (q.trim() !== "")
      pause.current = setTimeout(() => setFrameAsk((a) => ({ n: (a?.n ?? 0) + 1, query: q })), SEARCH_PAUSE_MS);
  }, []);
  // Enter: at once.
  const onSearchDone = useCallback((q: string) => {
    clearTimeout(pause.current);
    if (q.trim() !== "") setFrameAsk((a) => ({ n: (a?.n ?? 0) + 1, query: q }));
  }, []);
  // What's open: anything in the trip, as the plan has it when it's in the plan. Options and choices
  // aren't, but open too (Ideas' Show on map, a card's own): its card, with the choice's open options drawn
  // on the map as options, so nothing opens onto an empty map.
  const openItem = selected ? (items.find((i) => i.id === selected && i.status !== "dropped") ?? null) : null;
  const sel = openItem ? (visible.find((i) => i.id === openItem.id) ?? openItem) : null;
  // The choice whose options are on the map: the open option's or choice's own, else carried along from
  // the step before while things are opened in a row (a station tapped while comparing hotels).
  const selChoice = sel && (sel.type === "choice" || isOpenOption(sel)) ? choiceOf(sel, items) : null;
  const compare = sel ? (selChoice?.id ?? stepState().compare ?? null) : null;
  const compared = compare
    ? items.filter((i) => i.parent === compare && isOpenOption(i) && onMapTypes.has(i.type))
    : [];
  // What Today's "All on the map" asked to see together (`around`, its step's), framed as the map shows.
  const aroundIds = view === "map" && !sel ? (stepState().around ?? null) : null;

  // Every view and every tap is a step Back retraces. Closing what's open on the map (its X, Escape) lands
  // where it was opened from, the place still selected: its row lit in the map's list, or its small card.
  // Tapping the map, or a second Escape, clears the selection: back past the items opened in a row when
  // the first was opened from the map, else straight there as a new step, never to another view; one
  // opened by a link closes in place. Neither Back nor closing moves the map (TripMap). What's opened from
  // another view opens over it instead, and closes back to it (below). A view's tab opens it where it was
  // last left (kept.ts).
  const setView = (v: View) => navigate(tripPath(trip.id, null, v));
  // Not on the map at all (a to-do): its card opens in full, over the map on a phone, in the panel on a
  // computer; there's no place to show it at.
  const offMap = (id: string | null) => {
    const i = id ? items.find((x) => x.id === id) : undefined;
    return i !== undefined && !onMapTypes.has(i.type) && i.type !== "choice";
  };
  // What's open shows in full: opened by a click on a row or a pin on a computer, or a row of a phone's
  // list (features/map.md, "One click or tap to an item's card"), from the small card, from elsewhere to be
  // read (`card=full`: another view, a notification, an AI's link), an AI's upload link, or a to-do. On a
  // computer in the list's place at the map's right, at every width; on a phone over the map. Otherwise
  // it's selected, in brief: its row lit in the list and its small card on the map.
  const inFull = sel !== null && (full || attach === "1" || attach === "photo" || offMap(sel.id));
  // How far the map's list is scrolled, where it shows (beside the map, or over it on a phone).
  const listTop = () => {
    const list = document.querySelector(".tm-overview")?.closest<HTMLElement>(".tm-panel, .tm-layer-body");
    return list ? Math.round(list.scrollTop) : undefined;
  };
  // Closing never moves the map (TripMap), nor does opening what's selected already (its card from its
  // small card, Show on map from its card over the map, the list over it): a step they make keeps where the
  // map is, so a reload opens there, not on the place up close as a step just opened onto it would.
  const stayPut = (): StepState => {
    const { camera } = stepState();
    return camera ? { camera } : {};
  };
  // A step opening `id`, in brief or in full, one more in a row when something's open already. A card in
  // full opened from the map notes how the list was, so the list comes back as it was when the card, which
  // takes its place, closes to it.
  const open = (id: string, inFull: boolean, more: StepState = {}) => {
    const step = stepState();
    const carry = compare ? { compare } : {};
    const state = selected
      ? { opened: (step.opened ?? 0) + 1, fromList: step.fromList ?? false, ...carry }
      : { opened: 1, fromList: view === "map" };
    const top = inFull && view === "map" ? (listTop() ?? step.listTop) : undefined;
    const still = view === "map" && id === selected ? stayPut() : {};
    navigate(tripPath(trip.id, id, null, { full: inFull }), {
      state: { ...state, ...still, ...(top !== undefined && { listTop: top }), ...more },
    });
  };
  // A tap on the map (a phone's pin), or Show on map: shown in brief (a to-do, which has no place, in
  // full; on a phone over the map, so closing it is going Back). Null closes what's open.
  const select = (id: string | null) => {
    const step = stepState();
    const opened = step.opened ?? 0;
    if (id) open(id, offMap(id), offMap(id) && !wide ? { layer: true } : {});
    else if (opened > 0 && step.fromList) history.go(-opened);
    else if (opened > 0) navigate(tripPath(trip.id));
    else navigate(tripPath(trip.id), { replace: true });
  };
  // On a computer, a click on a row or a pin opens its card in full at once, selected (its row lit, its pin
  // selected, the map moving to it): a step of its own, so Back retraces each one clicked. The selected one
  // in brief (its row, its pin, its small card) opens again, marked so its X goes Back to it in brief. On a
  // phone, a pin shows its small card first (a row of the list opens the card: `openFromRow`).
  const pick = (id: string) => {
    if (!wide) return select(id);
    if (id !== selected) return open(id, true);
    if (!inFull) open(id, true, { fromBrief: true });
  };
  // On a phone, a row tapped in the list over the map: its card in full over the map, the sheet a card
  // over a view is, its X back to the list as it was, the row lit (`closeToList`).
  const openFromRow = (id: string) => open(id, true, { layer: true, fromRow: true });
  // On a computer, closing a card in full (its X, a first Escape): the place still selected, in brief, its
  // row lit in the list and its small card on the map; the list as it was when the card took its place.
  // Back, when it was opened from there; else a step to it (in place, when a link opened it).
  const closeFull = () => {
    const step = stepState();
    if (!sel || offMap(sel.id)) return select(null);
    if (step.fromBrief) return history.back();
    const opened = step.opened ?? 0;
    const kept = { ...stayPut(), ...(step.listTop !== undefined && { lists: { "panel-list": step.listTop } }) };
    const state = { opened: opened + 1, fromList: step.fromList ?? false, ...(compare ? { compare } : {}) };
    if (opened > 0) navigate(tripPath(trip.id, sel.id), { state: { ...state, ...kept } });
    else navigate(tripPath(trip.id, sel.id), { replace: true, state: kept });
  };
  // From a card on the map (what's near it, its options, its to-dos): in full on a computer; on a phone,
  // as a tap on the map shows it.
  const openFromCard = (id: string) => (wide ? open(id, true) : select(id));
  // Whether the map can show it: something with a place (a choice by its options), in the plan or not, a
  // cancelled booking too (off the map until it's shown there, then drawn while it's selected: onMap, below).
  // Nothing offers the map and then shows nothing (DESIGN.md, Views: Ideas).
  const onTheMap = (i: Item) =>
    i.type === "choice"
      ? items.some((o) => o.parent === i.id && isOpenOption(o) && onMapTypes.has(o.type) && point(o) !== null)
      : onMapTypes.has(i.type) && point(i) !== null;

  // Over another view (Days, People, Ideas, Bookings, Updates), what's tapped opens its card in full over
  // that view, a step of its own with an address naming the view (`?view=bookings&item=<id>&card=full`),
  // and the view marks it until another is opened there (DESIGN.md, "An item opens over the view I'm
  // on"). On a phone it's the sheet the map's card in full is; on a computer a panel at the side, the view
  // beside it. Its close, Escape and Back land on the view where it was, never on the map.
  // Today, open from the day before the trip to the day after (an address kept from outside it shows the view
  // alone), and a card opened from it over it.
  const todayWindow = useTodayWindow(trip) && !viewing;
  const todayOpen = today && todayWindow;
  const overToday = todayOpen && sel !== null;
  const overView = view !== "map" && !settings && !rate && !todayOpen && sel !== null;
  // An option's card over a view, opened from its tile on Bookings (Options.tsx): on a computer a modal over
  // the whole page, as a finding is (Modal.tsx; DESIGN.md, Bookings: "which the tile opens as a finding
  // opens"), its X, the scrim, Escape and Back closing it as the panel's close would; on a phone the sheet a
  // card over a view is.
  const optionModal = wide && overView && sel !== null && isOptionTile(sel, items);
  const marked = view !== "map" ? markedIn(view) : null;
  // What the card over a view was opened from, so closing it gives it the focus back.
  const opener = useRef<HTMLElement | null>(null);
  // `fromCard`: from inside the card over the view (what's near it, its options, its to-dos), one more
  // step over the same view, which closing goes back past too; the view keeps the one it opened marked.
  const openOverView = (id: string, fromCard = false) => {
    if (view === "map") return;
    const step = stepState();
    if (!fromCard) {
      markOpened(view, id);
      const active = document.activeElement;
      opener.current = active instanceof HTMLElement && active.closest(".tm-view-main") ? active : null;
    }
    // How many steps back the view is: one more for each card opened over it; none known when a link
    // opened the first (closing it then leaves it in place).
    const opened = !selected ? 1 : step.opened ? step.opened + 1 : undefined;
    navigate(tripPath(trip.id, id, view, { full: true }), { state: opened ? { opened } : {} });
  };
  const closeOverView = () => {
    const opened = stepState().opened ?? 0;
    if (opened > 0) history.go(-opened);
    else navigate(tripPath(trip.id, null, view), { replace: true });
  };
  // A finding read in full (features/research.md, "A tile of one height, opened to be read in full"): a
  // step of its own over the view it's opened from (`?view=ideas&research=<id>`), from its tile on Ideas or
  // on a card. On a computer it's a modal over the whole page (ResearchModal: a panel over a scrim; a card
  // it was opened from leaves the address, and Back brings it back), on a phone the whole screen as a card
  // over a view is. Its close, a click on the scrim, Escape and Back return to where it was opened, the view
  // where it was and the tile lit; opened by a link, its close lands on the view (Ideas, for an AI's link).
  // What's opened from it (what it's about) opens over it, a step of its own like a card over a view,
  // closing back to it.
  const finding = (r: Research): Finding => ({
    research: r,
    about: r.about.flatMap((id) => items.find((i) => i.id === id) ?? []),
    notes: notes.filter((n) => n.on === r.id),
  });
  const reader = reading ? (research.find((r) => r.id === reading) ?? null) : null;
  const fromReader = readerItem ? (items.find((i) => i.id === readerItem && i.status !== "dropped") ?? null) : null;
  // What opened the finding, and the card over it, so closing gives the focus back.
  const readerOpener = useRef<HTMLElement | null>(null);
  const readerCardOpener = useRef<HTMLElement | null>(null);
  // The address of what's under a finding being read: the view as it was, and whatever was open on it (a
  // card it was opened from stays there, under the scrim, so closing the finding finds it as it was).
  const under = (more: { research?: string | null; about?: string | null } = {}) =>
    tripPath(trip.id, selected, view, { rate, list, full: full && selected !== null, ...more });
  // What a step over it carries of the step under it, so what's under stays as it was: where the map is,
  // the choice whose options it shows, the deck's run so far.
  const keepUnder = (): StepState => {
    const { camera, compare, deck } = stepState();
    return { ...(camera && { camera }), ...(compare && { compare }), ...(deck !== undefined && { deck }) };
  };
  const openReader = (id: string) => {
    // From the card of what it's about, opened from the very finding: back to it.
    if (id === reading && fromReader) return closeOverReader();
    // From the view itself (not a card over it): the view marks it, as what's opened from it.
    if (view !== "map" && !selected && !reading && !rate) markOpened(view, id);
    const active = document.activeElement;
    readerOpener.current = active instanceof HTMLElement ? active : null;
    navigate(under({ research: id }), { state: { ...keepUnder(), layer: true } });
  };
  const closeReader = () => (stepState().layer ? history.back() : navigate(under(), { replace: true }));
  // `fromCard`: from inside the card over it, one more step, which closing goes back past too.
  const openOverReader = (id: string, fromCard = false) => {
    const step = stepState();
    if (!fromCard) {
      const active = document.activeElement;
      readerCardOpener.current = active instanceof HTMLElement ? active : null;
    }
    const opened = !fromReader ? 1 : step.opened ? step.opened + 1 : undefined;
    navigate(under({ research: reading, about: id }), { state: { ...keepUnder(), ...(opened && { opened }) } });
  };
  const closeOverReader = () => {
    const opened = stepState().opened ?? 0;
    if (opened > 0) history.go(-opened);
    else navigate(under({ research: reading }), { replace: true });
  };
  // The deck (DESIGN.md, "Rating one at a time"): its own address, a step over where it's started from
  // (Ideas' Waiting for you, People's "Not rated by you"), so Back and its close return there; opened by a
  // link (the round-up's), its close lands on Ideas. Tapping its card opens the card in full over it, a
  // step of its own whose close comes back to the same card; so does anything opened from that card or
  // from the map beside it.
  const deck = useDeck({ open: rate, trip, items, onChanged });
  const overDeck = rate && sel !== null;
  const startRating = () => navigate(tripPath(trip.id, null, "ideas", { rate: true }), { state: { layer: true } });
  const closeDeck = () =>
    stepState().layer ? history.back() : navigate(tripPath(trip.id, null, "ideas"), { replace: true });
  // Each card in full opened over the deck counts how many steps it is from the deck's own (`overDeck`), while
  // they follow on from it; a card opened by its link doesn't, nor what's opened from it.
  const openOverDeck = (id: string) => {
    const from = stepState().overDeck;
    const depth = !overDeck ? 1 : from ? from + 1 : undefined;
    navigate(tripPath(trip.id, id, "ideas", { rate: true, full: true }), {
      state: { layer: true, ...(depth && { overDeck: depth }), ...(deck.run && { deck: deck.run }) },
    });
  };
  // Rating the deck's card on top from its card in full answers the deck (DESIGN.md, "Rating one at a
  // time"; the founder, 2026-10-22: "the details page automatically dismisses, and the next card will be
  // shown"): the deck takes the answer as if it were swiped, and every card in full opened over it closes,
  // back to the deck's own step, so Back from there leaves the deck rather than reopening the card. A card
  // that isn't the one on top (a pin beside the deck, a place near it) is rated as anywhere else.
  const answerFromCard = (id: string) => (tier: Tier) => {
    if (!deck.decide(tier, "none", id)) return false;
    const depth = stepState().overDeck;
    if (depth) history.go(-depth);
    else navigate(tripPath(trip.id, null, "ideas", { rate: true }), { replace: true });
    return true;
  };
  const closeOverDeck = () =>
    stepState().layer ? history.back() : navigate(tripPath(trip.id, null, "ideas", { rate: true }), { replace: true });
  // What the deck shows on top: on a computer, the place on the map beside it.
  const deckTop = useMemo(() => {
    if (!rate || !deck.run) return null;
    const shows = deckShows(deck.run, new Map(items.map((i) => [i.id, i])), trip.you);
    return shows.kind === "card" ? (items.find((i) => i.id === shows.ids[0]) ?? null) : null;
  }, [rate, deck.run, items, trip.you]);
  // Today (DESIGN.md, Views, "Today, during the trip"): a step of its own over the view I'm on, so Back and
  // its X return to where I was; opened by a link, its X lands on the view. What's tapped in it opens its card
  // in full over it, a step of its own whose close comes back to Today; so does anything opened from that card
  // or, on a computer, from the map beside it. What it starts (the deck at an area, Days, the map with what we
  // want around us) is a step on from it, so Back comes back to Today.
  const todayCardOpener = useRef<HTMLElement | null>(null);
  const openToday = () => {
    navigate(tripPath(trip.id, null, view, { list: !wide && list, today: true }), {
      state: { ...keepUnder(), layer: true },
    });
  };
  const closeToday = () =>
    stepState().layer ? history.back() : navigate(tripPath(trip.id, null, view, { list }), { replace: true });
  const openOverToday = (id: string, fromCard = false) => {
    if (!fromCard) {
      const active = document.activeElement;
      todayCardOpener.current = active instanceof HTMLElement && active.closest(".tm-today") ? active : null;
    }
    navigate(tripPath(trip.id, id, view, { today: true, full: true, list: !wide && list }), {
      state: { ...keepUnder(), layer: true },
    });
  };
  const closeOverToday = () =>
    stepState().layer
      ? history.back()
      : navigate(tripPath(trip.id, null, view, { today: true, list: !wide && list }), { replace: true });
  const rateFromToday = (run: DeckRun) =>
    navigate(tripPath(trip.id, null, "ideas", { rate: true }), { state: { layer: true, deck: run } });
  const allDaysFromToday = () => navigate(tripPath(trip.id, null, "days"));
  const allOnMapFromToday = (ids: string[]) => navigate(tripPath(trip.id), { state: { around: ids } });
  // Opened by an address kept from outside its window (the trip reopened after it): the view alone.
  useEffect(() => {
    if (today && !todayWindow) navigate(tripPath(trip.id, selected, view, { list, full }), { replace: true });
  }, [today, todayWindow, trip.id, selected, view, list, full]);
  // Show on map, from a card over a view and on an option's tile (Bookings): the map with it selected in
  // brief, its row lit on a computer (with a choice's options drawn beside it); Back comes back to where it
  // was shown from. From a tile, it's opened from the view too, so the view marks it; with no place yet, its
  // card opens over the view and says so.
  const showOnMap = (id: string) => {
    const item = items.find((i) => i.id === id);
    if (view !== "map" && !selected && item && !onTheMap(item)) return openOverView(id);
    if (view !== "map" && !selected) markOpened(view, id);
    select(id);
  };
  // The same for every render, so the list's rows and the map's pins are drawn again only when they change.
  // Beside the deck, a pin opens over it, and tapping the map leaves it be.
  const pickRef = useRef(pick);
  pickRef.current = rate ? openOverDeck : todayOpen ? (id: string) => openOverToday(id) : pick;
  const selectRef = useRef(select);
  selectRef.current = rate
    ? (id: string | null) => id && openOverDeck(id)
    : todayOpen
      ? (id: string | null) => id && openOverToday(id)
      : select;
  const rowRef = useRef(openFromRow);
  rowRef.current = wide ? pick : openFromRow;
  const pickStable = useCallback((id: string | null) => (id ? pickRef.current(id) : selectRef.current(null)), []);
  const selectFromList = useCallback((id: string) => rowRef.current(id), []);
  // What the pointer is over, on a computer, in the list or on the map beside it (hover.ts).
  const [hover] = useState(() => new Hovered());
  // On a phone, the list and a card in full open over the map, each a step of its own (a card in full
  // counts as one more step opened, so closing goes back past it too); closing one is going Back, or
  // dropping it in place when it came with the address.
  // The list may open over a place in brief too, which stays selected under it (features/map.md).
  // Over another view, the card in full over it ("view").
  const over =
    wide || settings
      ? null
      : rate
        ? overDeck
          ? "deck-card"
          : "deck"
        : todayOpen
          ? overToday
            ? "today-card"
            : "today"
          : view !== "map"
            ? overView
              ? "view"
              : null
            : sel && (full || attach === "1" || attach === "photo" || offMap(sel.id))
              ? "full"
              : list
                ? "list"
                : null;
  // The map stays where it is underneath (it never moves on a step: TripMap).
  const openOver = (what: "list" | "full") => {
    const step = stepState();
    navigate(tripPath(trip.id, what === "full" ? selected : null, null, { [what]: true }), {
      state:
        what === "full"
          ? {
              ...stayPut(),
              opened: (step.opened ?? 0) + 1,
              fromList: step.fromList ?? false,
              layer: true,
              ...(compare ? { compare } : {}),
            }
          : { ...stayPut(), layer: true },
    });
  };
  // A card that's only ever in full (a to-do) closes as its own step would.
  const closeOver = () =>
    stepState().layer
      ? history.back()
      : over === "full" && !full && attach !== "1"
        ? select(null)
        : navigate(tripPath(trip.id, selected), { replace: true, state: stayPut() });
  // On a phone, the X of a card opened from a row of the list over the map: back to the list as it was,
  // the row lit and in view, the place still selected under it (`?item=<id>&list=1`; reported 2026-10-19
  // by the founder: the selection was gone). The card's step becomes the place in brief and the list
  // opens over it, a step of its own, so closing the list (Map, its X, Back) shows the small card, and
  // Back from there retraces as before. A to-do, with no place, has nothing to keep selected.
  const closeToList = () => {
    if (!sel || offMap(sel.id)) return history.back();
    const { opened, fromList, listTop: top } = stepState();
    const kept = {
      ...stayPut(),
      ...(fromList !== undefined && { fromList }),
      ...(compare && { compare }),
    };
    navigate(tripPath(trip.id, sel.id), {
      replace: true,
      state: { ...kept, ...(opened !== undefined && { opened }) },
    });
    navigate(tripPath(trip.id, sel.id, null, { list: true }), {
      state: {
        ...kept,
        opened: (opened ?? 0) + 1,
        layer: true,
        ...(top !== undefined && { lists: { "layer-list": top } }),
      },
    });
  };
  // The X (and Escape) of a card in full over a phone's map: back to the list when it was opened from a
  // row there, else as its step came (the small card, or in place when a link opened it).
  const closeCard = () => (stepState().fromRow ? closeToList() : closeOver());
  // A to-do removed from its card, or anything removed from the trip from its own (RemoveFromTrip.tsx): back where it
  // was opened from, with Undo there. Its card is closing, not a link to something gone: no "no longer in the trip"
  // while it closes (`justRemoved`, until what's open is something else).
  const [justRemoved, setJustRemoved] = useState<string | null>(null);
  useEffect(() => {
    if (justRemoved && selected !== justRemoved && readerItem !== justRemoved) setJustRemoved(null);
  }, [justRemoved, selected, readerItem]);
  const onRemoved = (r: Removed) => {
    setJustRemoved(r.id);
    setJustDone({ change: r.change, text: `Removed ${r.title}` });
    if (selected === r.id) {
      if ((stepState().opened ?? 0) > 0) history.back();
      else if (overView) closeOverView();
      else select(null);
    } else if (readerItem === r.id) closeOverReader();
  };
  // Trip settings: a step of its own over the view it's opened from, so Back (or its close, or Escape)
  // returns there; opened by a link, it closes in place.
  const openSettings = () => navigate(tripPath(trip.id, null, view, { settings: true }), { state: { layer: true } });
  const closeSettings = () =>
    stepState().layer ? history.back() : navigate(tripPath(trip.id, null, view), { replace: true });
  // Leaving or deleting the trip from its settings: what this page hears of it going isn't news.
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    // A card in full closes first, then the selection. Over another view, the card closes; with nothing
    // open over it, a view stays.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (booking) setBooking(null);
      else if (settings) closeSettings();
      else if (readerItem) closeOverReader();
      else if (reading) closeReader();
      else if (overDeck) closeOverDeck();
      else if (rate) closeDeck();
      else if (overToday) closeOverToday();
      else if (todayOpen) closeToday();
      else if (overView) closeOverView();
      else if (view !== "map") return;
      else if (over === "list") closeOver();
      else if (over === "full") closeCard();
      else if (wide && inFull) closeFull();
      else select(null);
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  });
  // The card over a view closed (its close, Escape, Back): the focus goes back to what opened it, else to
  // what the view marks, unless it went somewhere else meanwhile (a tab pressed).
  const overId = overView ? (sel?.id ?? null) : null;
  const wasOver = useRef(overId);
  useEffect(() => {
    const was = wasOver.current;
    wasOver.current = overId;
    if (!was || overId) return;
    const back = opener.current;
    opener.current = null;
    if (document.activeElement && document.activeElement !== document.body) return;
    const to = back?.isConnected ? back : document.querySelector<HTMLElement>(".tm-view-main [aria-current=true]");
    to?.focus({ preventScroll: true });
  });
  // The same for a finding read in full, closed: the focus goes back to its tile (or what else opened it),
  // else to the tile the view marks; and for a card over it, closed: to what it's about, lit in it.
  const readerKey = reader ? reader.id : null;
  const wasReading = useRef(readerKey);
  useEffect(() => {
    const was = wasReading.current;
    wasReading.current = readerKey;
    if (!was || readerKey) return;
    const back = readerOpener.current;
    readerOpener.current = null;
    // Once the card it was opened from has shown again, its title taking the focus as it does.
    // (Not cancelled by the next render: the page draws again at once as it settles.)
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && active !== document.body && !active.matches(".tm-details-head > h1")) return;
      const to = back?.isConnected
        ? back
        : document.querySelector<HTMLElement>(
            ".tm-details .tm-research-open[aria-current=true], .tm-view-main [aria-current=true]",
          );
      to?.focus({ preventScroll: true });
    });
  });
  const readerCardId = reader && fromReader ? fromReader.id : null;
  const wasReaderCard = useRef(readerCardId);
  useEffect(() => {
    const was = wasReaderCard.current;
    wasReaderCard.current = readerCardId;
    if (!was || readerCardId) return;
    const back = readerCardOpener.current;
    readerCardOpener.current = null;
    if (document.activeElement && document.activeElement !== document.body) return;
    const to = back?.isConnected ? back : document.querySelector<HTMLElement>(".tm-reader-text [aria-current=true]");
    to?.focus({ preventScroll: true });
  });
  // The same for a card in full on the map (its X, Escape, Back): the focus goes to the row lit in the
  // map's list, else the place's small card, unless it went somewhere else meanwhile (the map clicked).
  const mapCard = view === "map" && !settings && inFull ? (sel?.id ?? null) : null;
  const wasMapCard = useRef(mapCard);
  useEffect(() => {
    const was = wasMapCard.current;
    wasMapCard.current = mapCard;
    if (!was || mapCard) return;
    if (document.activeElement && document.activeElement !== document.body) return;
    const to =
      document.querySelector<HTMLElement>(".tm-overview .tm-row[aria-current=true]") ??
      document.querySelector<HTMLElement>(".tm-map-peek .tm-peek-open, .tm-phone-float .tm-peek-open");
    to?.focus({ preventScroll: true });
  });

  // Today opened: its heading takes the focus (the button it was opened from is gone). Closed (its X, Escape,
  // Back): back to the Today button, unless the focus went somewhere else meanwhile. A card over it closed:
  // back to the row it was opened from, else Today's heading.
  const todayKey = todayOpen ? (overToday ? `card:${sel?.id}` : "today") : null;
  const wasToday = useRef(todayKey);
  useEffect(() => {
    const was = wasToday.current;
    wasToday.current = todayKey;
    if (was === todayKey) return;
    const free = () => !document.activeElement || document.activeElement === document.body;
    const heading = () => document.querySelector<HTMLElement>("[data-today-heading]");
    requestAnimationFrame(() => {
      if (todayKey === "today" && was === null) heading()?.focus({ preventScroll: true });
      else if (todayKey === "today" && free()) {
        const back = todayCardOpener.current;
        todayCardOpener.current = null;
        (back?.isConnected ? back : heading())?.focus({ preventScroll: true });
      } else if (todayKey === null && free())
        document.querySelector<HTMLElement>(".tm-today-button")?.focus({ preventScroll: true });
    });
  });

  // Where I am (this screen only), and sharing it with the trip during it.
  const [locating, setLocating] = useState(false);
  const sharing = trip.members.find((m) => m.id === trip.you)?.sharing === true;
  const [shareBusy, setShareBusy] = useState(false);
  const where = useWhereIAm(locating);
  useShareWhereIAm(trip.id, where.fix, sharing && trip.location_window.open);

  // What the "What" filter lets through, on the map and in its list; a tapped item stays. A kind the
  // trip no longer has (its last one removed) doesn't count.
  const kindsHere = new Set(visible.map(kindOf));
  const showing = kinds.filter((k) => kindsHere.has(k));
  const ofKind =
    showing.length === 0 ? visible : visible.filter((i) => showing.includes(kindOf(i)) || i.id === sel?.id);
  // The list: what the search finds among that (features/map.md). Narrowed while a place is open, matching
  // or not: a place tapped on the map that doesn't match has no row to light.
  const listed = hits ? ofKind.filter((i) => hits.has(i.id)) : ofKind;
  // The ask, once the list and the map show what it asked about; whether anything it found is on the map.
  const searchFrame =
    frameAsk && hits && frameAsk.query === search
      ? { n: frameAsk.n, any: listed.some((i) => point(i) !== null) }
      : null;
  // On the map: everything the What filter shows, the search only dimming what it doesn't find, and the
  // options being compared (whatever the filters say: they were asked for).
  const comparedIds = new Set(compared.map((i) => i.id));
  // A cancelled booking is off the map, but shown on it from its card (Show on map) it's drawn while selected.
  const shownCancelled =
    sel && sel.status === "cancelled" && onMapTypes.has(sel.type) && point(sel) !== null && !comparedIds.has(sel.id)
      ? [sel]
      : [];
  const onMap = [...ofKind.filter((i) => !comparedIds.has(i.id)), ...compared, ...shownCancelled];
  // While searching, what the map shows at full strength: the matches, the tapped item and the options
  // being compared; the rest dims, drawn under them and never in a cluster with them.
  const searched = hits ? (i: Item) => hits.has(i.id) || i.id === sel?.id || comparedIds.has(i.id) : null;
  // Drawn as options: those being compared, and every choice's open ones, on the map anyway. Only those being
  // compared (their choice open, or shown from Ideas) stay out of clusters, each where it is.
  const isOption = (i: Item) => comparedIds.has(i.id) || optionsOf.has(i.id);
  const isApart = (i: Item) => comparedIds.has(i.id);
  const frame = compare
    ? { key: compare, ids: compared.map((i) => i.id) }
    : aroundIds
      ? { key: `around:${aroundIds.join(",")}`, ids: aroundIds }
      : null;
  const selPoint = sel ? point(sel) : null;
  // What "nearby" is measured from: the tapped place, or where I am when nothing's tapped.
  const myPoint: LngLat | null = where.fix ? [where.fix.lng, where.fix.lat] : null;
  const focus = selPoint ?? (sel ? null : myPoint);
  // How much is within reach of me, while I'm looking (the phone's list button says so).
  const nearMe = myPoint
    ? listed.filter((i) => {
        const at = point(i);
        return at !== null && distanceM(myPoint, at) <= NEARBY_M;
      }).length
    : null;

  const wants = (i: Item) =>
    i.interest.some((x) => x.tier && x.tier !== "no" && (person === "all" || x.person === person));
  const saidYes = (i: Item) => i.interest.some((x) => x.tier === "yes" && (person === "all" || x.person === person));
  const opacity = (i: Item) => {
    // Stays and moves are everyone's: filters about wanting don't apply to them. An option is rated by
    // each of us, so they do.
    const shared = (i.type === "stay" || i.type === "move") && !optionsOf.has(i.id);
    if (!shared && person !== "all" && !wants(i)) return DIMMED;
    if (!shared && yesOnly && !saidYes(i)) return DIMMED;
    if (friend && !recommenders(i).includes(friend)) return DIMMED;
    const at = point(i);
    // Options being compared stay at full strength, however far apart.
    if (comparedIds.has(i.id)) return 1;
    if (focus && at && i.id !== sel?.id && distanceM(focus as LngLat, at) > NEARBY_M) return 0.55;
    return 1;
  };

  const suggestedPin = (i: Item) => waitingSuggestion(i, trip.members);
  // What I said Yes to: a cluster on the map with one inside says so.
  const myYes = (i: Item) => myTier(i, trip.you) === "yes";
  // Restored by its owner after this page heard it go: listening again, from what's on screen.
  const [restored, setRestored] = useState(0);
  const heard = useLive(trip.id, trip.last_change, trip.you, restored, !viewing);
  const fresh = heard.changes;
  const others = (heard.locations ?? trip.locations)
    .filter((l) => l.person !== trip.you)
    .flatMap((l) => {
      const person = trip.members.find((m) => m.id === l.person);
      return person ? [{ person, lat: l.lat, lng: l.lng, at: l.at }] : [];
    });
  const toggleSharing = async () => {
    setShareBusy(true);
    try {
      await api.share(trip.id, !sharing);
      if (!sharing) setLocating(true);
      onChanged();
    } finally {
      setShareBusy(false);
    }
  };
  // What waits on me, on the tabs (DESIGN.md, Views, "What waits on me leads"): on Bookings my to-dos (mine
  // or nobody's, those to book too) due within 7 days or overdue, and a choice's options waiting on my rating
  // (rated under their choice's row, since 2026-10-23); on Ideas the rest of what waits on my rating; on Updates
  // the questions waiting on my answer. Counted from the trip as loaded, so they follow every change of mine at
  // once and others' on Refresh. A read-only trip takes no rating, answer or tick, so nothing in it waits on me.
  const todayDate = localToday();
  const inTrip = membersNow(trip);
  const waits = !trip.locked && !viewing;
  const toRate = waits ? ratingWaits(items, trip.you) : { ideas: [], options: [] };
  const waitingOnYou = waits ? asks.filter((a) => a.waiting_on.some((p) => p.id === trip.you)).length : 0;
  const dueSoon = waits ? items.filter((i) => dueSoonFor(i, trip.you, todayDate, inTrip)).length : 0;
  const badges: Partial<Record<View, number>> = {
    bookings: dueSoon + toRate.options.length,
    ideas: toRate.ideas.length,
    // Requests to join wait on nobody while it's read-only: nobody can be let in. Nor does an offer of the trip to me,
    // which can't change hands then (DESIGN.md, Member, "Taking over a trip").
    people: waits ? trip.requests.length + (trip.offer?.to === trip.you ? 1 : 0) : 0,
    updates: fresh.length + waitingOnYou,
  };
  // Offline (or on a weak signal, or Tripmo not answering), the page says what it shows is what this phone
  // kept. A new version of Tripmo stands in for the changes banner: reloading brings those too.
  const online = useOnline();
  const kept = useKept();
  const newVersion = useNewVersion();
  const offline = !online || Boolean(kept);
  const banner = offline ? (
    <NoSignal what="the trip" />
  ) : newVersion ? (
    <NewVersion />
  ) : fresh.length > 0 ? (
    <LiveBanner key={fresh.at(-1)?.seq} text={liveText(fresh, trip.members, trip.you)} onRefresh={onChanged} />
  ) : null;
  // What I just did, with its Undo: first, since it's about what I just did.
  const undo = justDone && !offline && (
    <UndoNotice
      key={`${justDone.change}-${justDone.text}`}
      trip={trip.id}
      change={justDone.change}
      text={justDone.text}
      onUndone={onChanged}
      onGone={() => setJustDone((d) => (d === justDone ? null : d))}
    />
  );
  // Below them, the offer to bring the others in (not offline: putting it away needs Tripmo).
  const inviting = !offline && !invitePutAway && inviteDue(trip.invite, new Date());
  // Read-only (its Trip Pass refunded or disputed): said first, and for as long as it is.
  const locked = trip.locked ? <LockedNotice trip={trip} /> : null;
  // A link to something no longer in the trip (deleted, or dropped): the trip, saying so until OK. The same
  // for a finding, and for what's opened over it.
  const missing =
    selected && !openItem && selected !== justRemoved ? (
      <ItemGone
        key={selected}
        trip={trip}
        id={selected}
        dropped={items.find((i) => i.id === selected) ?? null}
        onDone={() => navigate(tripPath(trip.id, null, view, { rate }), { replace: true })}
      />
    ) : reading && !reader ? (
      <ItemGone
        key={reading}
        trip={trip}
        id={reading}
        dropped={null}
        onDone={() => navigate(under(), { replace: true })}
      />
    ) : readerItem && !fromReader && readerItem !== justRemoved ? (
      <ItemGone
        key={readerItem}
        trip={trip}
        id={readerItem}
        dropped={items.find((i) => i.id === readerItem) ?? null}
        onDone={() => navigate(under({ research: reading }), { replace: true })}
      />
    ) : null;
  // A shared trip's bar: on a phone a strip across the top of the screen, on every view (PhoneTrip); on a computer a
  // band over the trip's bar (below).
  const viewBar = viewing && !wide ? <ViewBar viewing={viewing} phone /> : null;
  // Shared to view and asking me whether viewers see my name, photo and ratings (features/sharing.md), until I
  // answer (not offline: answering needs Tripmo).
  const asked =
    !viewing &&
    !offline &&
    !settings &&
    trip.sharing?.on === true &&
    trip.sharing.mine === null &&
    trip.owner !== trip.you ? (
      <ViewersAsk trip={trip.id} onAnswered={onChanged} />
    ) : null;
  const live = (undo || missing || locked || banner || asked || inviting) && (
    <div className="tm-notices" ref={noticesRef}>
      {undo}
      {missing}
      {locked}
      {banner}
      {asked}
      {inviting && <InviteCard trip={trip} onPutAway={() => setInvitePutAway(true)} />}
    </div>
  );
  // Offline, the pins without the base map: its tiles are too big to keep.
  const tiles = online ? me.map.tiles : "";
  // A view's Add (Ideas, Days, Bookings; none on a read-only trip): its own changes on the web, and the asks
  // for its view, near the stay we're in or next go to on Ideas (by its city, when it has one).
  const nights = (i: Item) => (i.when?.precision === "days" ? i.when : null);
  const stayCity =
    items
      .filter((i) => i.type === "stay" && i.where?.city && (i.status === "booked" || i.status === "planned"))
      .sort((a, b) => (nights(a)?.start ?? "").localeCompare(nights(b)?.start ?? ""))
      .find((i) => (nights(i)?.end ?? "9999") >= todayDate)?.where?.city ?? null;
  const runViewEdit = (edit: ViewEdit) => {
    if (edit === "rate-ideas") startRating();
    else if (edit === "trip-dates") openSettings();
    else if (edit === "currency") navigate("/profile");
    else setViewEdit(edit);
  };
  const addFor = (v: AddView, look: "add" | "float") =>
    trip.locked || viewing ? null : (
      <AskAi
        trip={trip}
        edits={viewEdits(v, {
          toRate: toRate.ideas.length,
          unplaced: planned.filter((i) => plannable(i) && !hasDay(i.when)).length,
          currency: Boolean(me.currency),
        })}
        asks={viewAsks(v, stayCity)}
        onEdit={runViewEdit}
        look={look}
      />
    );
  // A card's "Change with your AI" on its small card: what it does on the web opens its card in full first.
  const peekAsk = (item: Item, open: () => void) => {
    if (viewing) return null;
    const of = choiceWith(item);
    return (
      <AskAi
        trip={trip}
        thing={item.title}
        edits={cardEdits(item, {
          choice: of?.choice ?? null,
          books: true,
          asks: asks.filter((a) => a.about?.id === item.id && a.status === "open" && askedOf(a, trip.you)).length,
          mine: asks.filter((a) => a.about?.id === item.id && a.status === "open" && a.by.id === trip.you).length,
          links: linkRows(item, links).length,
          readOnly: Boolean(trip.locked),
        })}
        asks={cardAsks(item, of?.choice ?? null)}
        onEdit={(edit) => {
          setStartEdit({ id: item.id, edit });
          open();
        }}
        look="icon"
      />
    );
  };
  // The views besides the map, and Trip settings over whichever it was opened from.
  const other = settings ? (
    <TripSettings trip={trip} onLeaving={setLeaving} onChanged={onChanged} />
  ) : view === "people" ? (
    <People
      trip={trip}
      items={planned}
      all={items}
      notes={notes}
      onOpen={openOverView}
      onChanged={onChanged}
      onStartRating={startRating}
    />
  ) : view === "days" ? (
    <Days
      trip={trip}
      items={planned}
      choices={choices(items, tripStart(trip.dates))}
      weather={weather}
      wide={wide}
      onOpen={openOverView}
      onChanged={onChanged}
      add={wide ? addFor("days", "add") : null}
      start={viewEdit === "plan-day"}
      onStarted={() => setViewEdit(null)}
    />
  ) : view === "bookings" ? (
    <Bookings
      trip={trip}
      items={planned}
      all={items}
      reminders={reminders}
      documents={documents}
      files={files.filter((f) => f.on.startsWith("doc_"))}
      attach={attach}
      onOpen={openOverView}
      onShow={showOnMap}
      onChanged={onChanged}
      add={wide ? addFor("bookings", "add") : null}
      start={viewEdit === "add-todo"}
      startDocument={viewEdit === "add-document"}
      onStarted={() => setViewEdit(null)}
    />
  ) : view === "ideas" ? (
    <Ideas
      trip={trip}
      items={items}
      links={links}
      pads={pads}
      entries={entries}
      research={research}
      notes={notes}
      onOpen={openOverView}
      onRead={openReader}
      onChanged={onChanged}
      onStartRating={startRating}
      add={wide ? addFor("ideas", "add") : null}
      start={viewEdit === "save-link"}
      onStarted={() => setViewEdit(null)}
    />
  ) : (
    <Updates trip={trip} items={items} asks={asks} onOpen={openOverView} onChanged={onChanged} />
  );

  // Friends whose picks are in the plan, most picks first.
  const pickCounts = new Map<string, number>();
  for (const i of [...planned, ...visible.filter((v) => optionsOf.has(v.id))])
    for (const f of recommenders(i)) pickCounts.set(f, (pickCounts.get(f) ?? 0) + 1);
  const friends = [...pickCounts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const filters = (
    <MapFilters
      // On a shared trip's view, only those whose ratings show, and none without Who wants what.
      members={viewing ? trip.members.filter((m) => !m.anonymous) : trip.members}
      whose={!viewing || (viewing.shares.has("ratings") && trip.members.some((m) => !m.anonymous))}
      you={trip.you}
      person={person}
      onPerson={setPerson}
      yesOnly={yesOnly}
      onYesOnly={setYesOnly}
      friends={friends}
      friend={friend}
      onFriend={setFriend}
      items={visible}
      kinds={kinds}
      onKinds={setKinds}
      wide={wide}
    />
  );

  // Where I am, in the map's corner like any map's "locate me"; sharing it beside, while locating or
  // sharing, during the trip.
  const locate = (
    <div className="tm-locate">
      {where.error && <p className="caption tm-locate-note">{where.error}</p>}
      <div className="tm-locate-row">
        {/* A read-only trip: stopping sharing still works, starting doesn't. */}
        {(locating || sharing) && trip.location_window.open && (sharing || !trip.locked) && (
          <Chip icon="people" selected={sharing} onClick={shareBusy ? () => {} : toggleSharing}>
            {sharing ? "Sharing with the trip" : "Share with the trip"}
          </Chip>
        )}
        <button
          type="button"
          className={`tm-map-button${locating ? " is-on" : ""}`}
          aria-label="Where I am"
          aria-pressed={locating}
          onClick={() => setLocating(!locating)}
        >
          <Icon name="pin" size={20} />
        </button>
      </div>
    </div>
  );

  // Beside the deck on a computer: the place on its card on top (or the card in full over it), selected on
  // the map with what's near it at full strength, and the rest of the trip, whatever the map's filters say.
  const deckSel = overDeck ? sel : deckTop;
  const deckAt = deckSel ? point(deckSel) : null;
  const deckMap = [
    ...visible.filter((i) => !comparedIds.has(i.id) && i.status !== "cancelled"),
    ...compared,
    ...(deckSel && deckAt && !visible.some((i) => i.id === deckSel.id) && !comparedIds.has(deckSel.id)
      ? [deckSel]
      : []),
  ];
  const deckOpacity = (i: Item) => {
    const at = point(i);
    if (comparedIds.has(i.id) || i.id === deckSel?.id || !deckAt || !at) return 1;
    return distanceM(deckAt, at) > NEARBY_M ? 0.55 : 1;
  };

  // The booking form is the open card's: closed with it, or when another card opens in its place.
  const bookingFor = booking ? (items.find((i) => i.id === booking) ?? null) : null;
  useEffect(() => {
    if (booking && booking !== sel?.id && booking !== fromReader?.id) setBooking(null);
  }, [booking, sel?.id, fromReader?.id]);
  const closeBooking = () => setBooking(null);
  // Saved: closed once the trip has loaded with it (or after a while all the same), with what was done.
  const [bookingSaved, setBookingSaved] = useState<{ id: string; version: number; saved: Saved } | null>(null);
  useEffect(() => {
    if (!bookingSaved) return;
    const done = () => {
      setBookingSaved(null);
      setBooking((b) => (b === bookingSaved.id ? null : b));
      if (bookingSaved.saved.text) setJustDone({ change: bookingSaved.saved.change, text: bookingSaved.saved.text });
    };
    const now = items.find((i) => i.id === bookingSaved.id);
    if (!now || now.version >= bookingSaved.version) return done();
    const timer = setTimeout(done, 5000);
    return () => clearTimeout(timer);
  }, [bookingSaved, items, setJustDone]);
  const bookingForm = (item: Item, bar?: ReactNode) => (
    <BookingForm
      key={item.id}
      item={item}
      trip={trip}
      items={items}
      {...(bar ? { bar } : {})}
      onClose={closeBooking}
      onChanged={onChanged}
      onSaved={(saved, version) => setBookingSaved({ id: item.id, version, saved })}
      saving={bookingSaved?.id === item.id}
      at={bookingAt}
    />
  );
  // The choice an open item belongs to (or is), with its options: its card lists them.
  const choiceWith = (i: Item) => choices(items).find((c) => c.choice.id === choiceOf(i, items)?.id) ?? null;
  const ofChoice = sel ? choiceWith(sel) : null;
  // An item's card in full, wherever it's open: what its head holds on a computer (on a phone, the bar over
  // it has them), and where what's opened from it opens.
  const details = (item: Item, bar: ReactNode, onSelect: (id: string) => void) => {
    const of = item === sel ? ofChoice : choiceWith(item);
    // On a computer its booking form takes the card's place in its panel, its close back to the card (in an
    // option's modal, the modal's own close closes both).
    if (wide && booking === item.id)
      return bookingForm(
        item,
        optionModal && item === sel ? undefined : (
          <span className="tm-details-actions">
            <button type="button" className="tm-layer-close" aria-label="Close the booking form" onClick={closeBooking}>
              <Icon name="close" size={20} />
            </button>
          </span>
        ),
      );
    return (
      <Details
        item={item}
        trip={trip}
        items={onMap}
        choice={of?.choice ?? null}
        options={of?.options ?? []}
        links={links}
        notes={notes.filter((n) => n.on === item.id)}
        asks={asks.filter((a) => a.about?.id === item.id && a.status === "open")}
        research={research.filter((r) => r.about.includes(item.id)).map(finding)}
        onRead={openReader}
        files={files.filter((f) => f.on === item.id)}
        todos={items.filter((t) => t.type === "task" && t.parent === item.id)}
        invited={item !== sel ? null : attach === "1" ? "file" : attach === "photo" ? "photo" : null}
        bar={bar}
        focus
        onSelect={onSelect}
        onChanged={onChanged}
        onRemoved={onRemoved}
        onAnswer={item === sel && overDeck && item.id === deckTop?.id ? answerFromCard(item.id) : undefined}
        onBook={(at) => {
          setBooking(item.id);
          setBookingAt(at ?? null);
        }}
        start={startEdit?.id === item.id ? startEdit.edit : null}
        onStarted={() => setStartEdit(null)}
      />
    );
  };
  const card =
    sel &&
    details(
      sel,
      // On a computer, its head: Share and its close; over another view, Show on map too (anything the map
      // can show). On the map it's there already: no Show on map, and no "Everything in the trip" (the
      // founder's pick, 2026-10-20: its X brings the list back).
      !wide ? undefined : (
        <>
          {(overView || (overToday && view !== "map")) && onTheMap(sel) && (
            <ShowOnMap onClick={() => showOnMap(sel.id)} />
          )}
          <span className="tm-details-actions">
            {!viewing && <ShareItem item={sel} trip={trip} compact={false} />}
            {/* In a modal, its own X at the panel's corner closes it. */}
            {!optionModal && (
              <CloseCard
                onClick={overToday ? closeOverToday : overDeck ? closeOverDeck : overView ? closeOverView : closeFull}
              />
            )}
          </span>
        </>
      ),
      overToday
        ? (id) => openOverToday(id, true)
        : overDeck
          ? openOverDeck
          : overView
            ? (id) => openOverView(id, true)
            : openFromCard,
    );
  // A card opened from a finding being read, over it, as a card over a view is: Show on map, Share, its close.
  const readerCard =
    reader &&
    fromReader &&
    details(
      fromReader,
      !wide ? undefined : (
        <>
          {onTheMap(fromReader) && <ShowOnMap onClick={() => select(fromReader.id)} />}
          <span className="tm-details-actions">
            {!viewing && <ShareItem item={fromReader} trip={trip} compact={false} />}
            <CloseCard onClick={closeOverReader} />
          </span>
        </>
      ),
      (id) => openOverReader(id, true),
    );
  // The finding being read, in full.
  const readerText = reader && (
    <ResearchReader
      finding={finding(reader)}
      trip={trip}
      edit={{ trip: trip.id, onChanged }}
      wide={wide}
      onOpen={(id) => openOverReader(id)}
    />
  );
  // Today, over the view (Today.tsx): from where I am while Where I am is on, else around tonight's stay.
  const todayEl = todayOpen && (
    <Today
      trip={trip}
      items={items}
      files={files}
      weather={weather}
      mornings={mornings}
      me={myPoint}
      wide={wide}
      onOpen={(id) => openOverToday(id)}
      onClose={closeToday}
      onRate={rateFromToday}
      onAllDays={allDaysFromToday}
      onAllOnMap={allOnMapFromToday}
      onChanged={onChanged}
    />
  );
  // The Today button: from the day before the trip to the day after, while nothing is open over the view (a
  // card, a place in brief, the deck, a finding, Trip settings) and Today isn't showing.
  const todayButton =
    todayWindow && !settings && !rate && !reader && !todayOpen && sel === null ? (
      <TodayButton onClick={openToday} />
    ) : null;
  const overview = (
    <>
      {/* The map's first-visit card, at the top of its list: beside the map on a computer, and on a phone
          in the list over it, so the map keeps all its room. With nothing in the trip, the list's own
          empty state says the same. */}
      <FirstVisit view="map" hidden={visible.length === 0} className="tm-first-visit-panel" />
      <Overview
        trip={trip}
        items={listed}
        anything={visible.length > 0}
        layout={layout}
        many={ofKind.filter((i) => point(i) !== null).length > FOLD_PAST}
        filtered={showing.length > 0}
        query={search}
        onQuery={onSearch}
        onQueryDone={onSearchDone}
        notes={notes.filter((n) => n.on === trip.id)}
        me={myPoint}
        selected={sel?.id ?? null}
        hover={wide ? hover : null}
        onSelect={selectFromList}
        onChanged={onChanged}
      />
    </>
  );

  // Its owner deleted it while it was open: the trip goes from this page too (unless this page did it).
  if (heard.gone && !leaving)
    return (
      <TripGone
        trip={heard.gone}
        you={trip.you}
        onRestored={() => {
          setRestored((n) => n + 1);
          onChanged();
        }}
      />
    );

  // The trip's name is its menu: Your trips, the other trips, Trip settings.
  const name = (className: string) =>
    viewing ? (
      <StaticTripName trip={trip} className={className} />
    ) : (
      <TripMenu trip={trip} className={className} settings={settings} onSettings={openSettings} />
    );
  // While Trip settings is open, no view's tab is the current one.
  const current = settings ? null : view;
  // On a computer, what's selected in brief, on the map: the phone's small card, at the map's foot.
  const brief =
    wide && sel && !inFull ? (
      <Peek
        item={sel}
        trip={trip}
        options={ofChoice?.options ?? []}
        onOpen={() => pick(sel.id)}
        onClose={() => select(null)}
        ask={peekAsk(sel, () => pick(sel.id))}
      />
    ) : null;
  // And where the pointer rests on a row or a pin, that place's small card in its place, opening it at once
  // (FootCards). Nothing on the map is a choice, so it has no options to count.
  const resting = (id: string) => {
    const item = (rate ? deckMap : onMap).find((i) => i.id === id);
    return item ? <Peek item={item} trip={trip} onOpen={() => pickRef.current(id)} /> : null;
  };

  if (wide) {
    // What's framed on the map keeps clear of what's at its foot: the small card, the notices above it.
    // Measured once it's drawn; until then (the frame that selects), about as tall as it is.
    const foot = [brief ? briefHeight || BRIEF_HEIGHT : 0, noticesHeight].filter((h) => h > 0);
    const footHeight =
      foot.length === 0 ? 24 : FOOT + foot.reduce((sum, h) => sum + h, 0) + 12 * (foot.length - 1) + 24;
    // A place the pointer rests on shows its small card there too: a hover's look keeps clear of it.
    const hoverFoot = FOOT + (briefHeight || BRIEF_HEIGHT) + (noticesHeight ? noticesHeight + 12 : 0) + 24;
    return (
      <div className="tm-trip-page">
        {viewing && (
          <div inert={reader !== null || optionModal}>
            <ViewBar viewing={viewing} phone={false} />
          </div>
        )}
        <header className="tm-bar tm-trip-bar" inert={reader !== null || optionModal}>
          {/* The wordmark and the trip's name keep to one line, the name ending with an ellipsis before
              either leaves the screen; the tabs and faces follow on the same line while there's room,
              and move to a line of their own together when there isn't. */}
          <div className="tm-bar-start">
            <Wordmark />
            {name("tm-trip-name")}
          </div>
          <div className="tm-bar-end">
            {/* Today beside the tabs, its place kept while it's hidden, so the tabs never move. */}
            {todayWindow && (
              <div className={`tm-bar-today${todayButton ? "" : " is-hidden"}`}>
                <TodayButton onClick={openToday} compact />
              </div>
            )}
            <nav className="tm-tabs" aria-label="Views">
              {views.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  className={`tm-tab${v.key === current ? " is-on" : ""}`}
                  aria-current={v.key === current ? "page" : undefined}
                  onClick={() => setView(v.key)}
                >
                  {v.label}
                  <Badge count={badges[v.key] ?? 0} />
                </button>
              ))}
            </nav>
            <AvatarStack people={trip.members} size={32} />
          </div>
        </header>
        {/* What's under the trip's bar: the map, the deck or a view. Under a finding read in full, it and the
            bar are out of reach (inert), the trip's notices showing over the finding instead. */}
        <div className="tm-trip-stage" inert={reader !== null || optionModal}>
          {view === "map" && !settings ? (
            <CaughtView className="tm-split" what="the map" view="map">
              <main
                className="tm-map-area"
                aria-label="Map of the trip"
                // At the map's foot: the small card, the notices above it.
                style={{ "--tm-foot": `${FOOT}px` } as CSSProperties}
              >
                <TripMap
                  trip={trip.id}
                  items={onMap}
                  selected={sel?.id ?? null}
                  opacity={opacity}
                  suggested={suggestedPin}
                  option={isOption}
                  apart={isApart}
                  myYes={myYes}
                  frame={frame}
                  searchFrame={searchFrame}
                  searched={searched}
                  onSelect={pickStable}
                  theme={theme}
                  tiles={tiles}
                  glyphs={me.map.glyphs}
                  padding={{ top: 72, bottom: footHeight, left: 24, right: 24 }}
                  me={where.fix}
                  others={others}
                  corner={locate}
                  hover={hover}
                  hoverFoot={hoverFoot}
                />
                <div className="tm-map-top">{filters}</div>
                <div className="tm-map-foot">
                  {reader ? null : live}
                  <FootCards
                    hover={hover}
                    brief={brief}
                    briefRef={briefRef}
                    selected={sel?.id ?? null}
                    resting={resting}
                  />
                </div>
              </main>
              {/* The map's list at its right; a card in full takes its place, at every width (features/map.md,
                "One click or tap to an item's card"). */}
              <KeptPanel
                className="tm-panel"
                name={
                  todayOpen
                    ? overToday && sel
                      ? `today-${sel.id}`
                      : "panel-today"
                    : inFull && sel
                      ? `panel-${sel.id}`
                      : "panel-list"
                }
                label={
                  todayOpen
                    ? overToday && sel
                      ? sel.title
                      : "Today"
                    : inFull && sel
                      ? sel.title
                      : "Everything in the trip"
                }
              >
                {todayOpen ? (overToday ? card : todayEl) : inFull ? card : overview}
              </KeptPanel>
            </CaughtView>
          ) : rate ? (
            <CaughtView className="tm-split" what="Ideas" view="rate">
              <main
                className="tm-map-area"
                aria-label="Map of the trip"
                style={{ "--tm-foot": `${FOOT}px` } as CSSProperties}
              >
                <TripMap
                  trip={trip.id}
                  items={deckMap}
                  selected={deckSel?.id ?? null}
                  opacity={deckOpacity}
                  suggested={suggestedPin}
                  option={isOption}
                  apart={isApart}
                  myYes={myYes}
                  frame={frame}
                  searchFrame={null}
                  searched={null}
                  onSelect={pickStable}
                  theme={theme}
                  tiles={tiles}
                  glyphs={me.map.glyphs}
                  padding={{ top: 24, bottom: footHeight, left: 24, right: 24 }}
                  me={where.fix}
                  others={others}
                  corner={locate}
                  hover={hover}
                  hoverFoot={hoverFoot}
                />
                <div className="tm-map-foot">
                  {reader ? null : live}
                  <FootCards
                    hover={hover}
                    brief={null}
                    briefRef={briefRef}
                    selected={deckSel?.id ?? null}
                    resting={resting}
                  />
                </div>
              </main>
              {/* The deck in the panel at the map's right, where the list and a place's card sit; a card it
                opens in full takes its place, closing back to it. */}
              <KeptPanel
                className={overDeck ? "tm-panel" : "tm-panel is-deck"}
                name={overDeck && sel ? `deck-${sel.id}` : "panel-deck"}
                label={overDeck && sel ? sel.title : "Rating ideas"}
              >
                {overDeck
                  ? card
                  : deck.run && (
                      <Deck deck={deck} trip={trip} items={items} wide onOpen={openOverDeck} onClose={closeDeck} />
                    )}
              </KeptPanel>
            </CaughtView>
          ) : (
            <>
              {/* Another view, and what's opened from it in a panel at its side, as wide as the map's list. */}
              <CaughtView className="tm-view" what={viewWords(current)} view={current ?? "settings"}>
                {/* On a computer the notices float over the view's foot: it scrolls on past them, so its last
                    rows can be read (a read-only trip's notice stays for good). */}
                <div
                  className={`tm-view-main${noticesHeight && live ? " clears-notices" : ""}`}
                  style={
                    noticesHeight && live
                      ? ({ "--tm-notices-clear": `${noticesHeight + 24}px` } as CSSProperties)
                      : undefined
                  }
                >
                  <LastOpened value={marked}>{other}</LastOpened>
                </div>
                {/* A card is raised over the view (DESIGN.md, "An item opens over the view I'm on"); Today's own
                    panel keeps the page's colour, its rows raised on it. */}
                {overView && sel && !optionModal && (
                  <KeptPanel className="tm-panel tm-view-panel is-card" name={`card-${sel.id}`} label={sel.title}>
                    {card}
                  </KeptPanel>
                )}
                {todayOpen && (
                  <KeptPanel
                    className={`tm-panel tm-view-panel${overToday && sel ? " is-card" : ""}`}
                    name={overToday && sel ? `today-${sel.id}` : "today"}
                    label={overToday && sel ? sel.title : "Today"}
                  >
                    {overToday ? card : todayEl}
                  </KeptPanel>
                )}
              </CaughtView>
              {reader || optionModal ? null : live}
            </>
          )}
        </div>
        {/* An option's card in full, a modal over all of it, the notices above the scrim; under a finding
            opened from it, out of reach. */}
        {/* One shell as one option after another is opened in it (its other options), so it doesn't fade in
            again for each. */}
        {optionModal && sel && (
          <Modal
            name={`card-${sel.id}`}
            label={sel.title}
            className="is-card"
            onClose={closeOverView}
            onScrim={closeOverView}
            notices={reader ? null : live}
            inert={reader !== null}
          >
            {card}
          </Modal>
        )}
        {/* A finding read in full, a modal over all of it; what's opened from it above it, and the notices
            above the scrim. */}
        {reader && (
          <Modal
            key={reader.id}
            name={`reader-${reader.id}`}
            label={reader.title}
            onClose={closeReader}
            onScrim={readerCard ? closeOverReader : closeReader}
            card={
              readerCard &&
              fromReader && (
                <KeptPanel className="tm-panel tm-modal-card" name={`read-${fromReader.id}`} label={fromReader.title}>
                  {readerCard}
                </KeptPanel>
              )
            }
            notices={live}
          >
            {readerText}
          </Modal>
        )}
      </div>
    );
  }

  // On a phone, what's over the view or the map: the deck, the list or a card in full (as a step brings it);
  // and over that, a finding read in full, the sheet a card over a view is, and a card opened from it (on a
  // phone the screen is a sheet already: a finding is one more, not a modal).
  const baseOver =
    over === "today" ? (
      // Today, a sheet over the view or the map, its X at the top.
      <section key="today" className="tm-phone-layer" aria-label="Today">
        <div className="tm-layer-bar">
          <div className="tm-layer-actions">
            <button type="button" className="tm-layer-close" aria-label="Close Today" onClick={closeToday}>
              <Icon name="close" size={20} />
            </button>
          </div>
        </div>
        <KeptPanel className="tm-layer-body" name="today">
          {todayEl}
        </KeptPanel>
      </section>
    ) : over === "deck" ? (
      // The deck, a sheet over the view it was started from.
      <section key="deck" className="tm-phone-layer is-deck" aria-label="Rating ideas" inert={reader !== null}>
        {deck.run && (
          <Deck deck={deck} trip={trip} items={items} wide={false} onOpen={openOverDeck} onClose={closeDeck} />
        )}
      </section>
    ) : (
      over && (
        <section
          // A sheet of its own for each: closing a card to the list gives the focus to the row lit there,
          // not to the list's own close in the same place.
          key={over === "list" ? "list" : `card-${sel?.id}`}
          className="tm-phone-layer"
          aria-label={over !== "list" && sel ? sel.title : "Everything in the trip"}
          // Under a finding being read, or its booking form, out of reach as the view is.
          inert={reader !== null || bookingFor !== null}
        >
          {/* The list's title, or the card's way to the map (the card is the same sheet over the map and over
          another view: Show on map, Share and its X; never "Everything in the trip", since 2026-10-20). */}
          <div className="tm-layer-bar">
            {over === "list" ? (
              <h1 className="title">Everything in the trip</h1>
            ) : (
              sel && onTheMap(sel) && <ShowOnMap onClick={() => showOnMap(sel.id)} />
            )}
            <div className="tm-layer-actions">
              {over !== "list" && sel && !viewing && <ShareItem item={sel} trip={trip} compact />}
              <button
                type="button"
                className="tm-layer-close"
                aria-label={over === "list" ? "Close the list" : "Close"}
                onClick={
                  over === "today-card"
                    ? closeOverToday
                    : over === "deck-card"
                      ? closeOverDeck
                      : over === "view"
                        ? closeOverView
                        : over === "full"
                          ? closeCard
                          : closeOver
                }
              >
                <Icon name="close" size={20} />
              </button>
            </div>
          </div>
          <KeptPanel
            className="tm-layer-body"
            name={
              over === "today-card" && sel
                ? `today-${sel.id}`
                : over === "deck-card" && sel
                  ? `deck-${sel.id}`
                  : over === "view" && sel
                    ? `card-${sel.id}`
                    : over === "full" && sel
                      ? `layer-${sel.id}`
                      : "layer-list"
            }
          >
            {over === "list" ? overview : card}
          </KeptPanel>
        </section>
      )
    );
  const readerSheets = reader && (
    <>
      <section key={`reader-${reader.id}`} className="tm-phone-layer" aria-label={reader.title}>
        <div className="tm-layer-bar">
          <div className="tm-layer-actions">
            <button type="button" className="tm-layer-close" aria-label="Close" onClick={closeReader}>
              <Icon name="close" size={20} />
            </button>
          </div>
        </div>
        <KeptPanel className="tm-layer-body" name={`reader-${reader.id}`}>
          {readerText}
        </KeptPanel>
      </section>
      {readerCard && fromReader && (
        <section key={`read-${fromReader.id}`} className="tm-phone-layer" aria-label={fromReader.title}>
          <div className="tm-layer-bar">
            {onTheMap(fromReader) && <ShowOnMap onClick={() => select(fromReader.id)} />}
            <div className="tm-layer-actions">
              {!viewing && <ShareItem item={fromReader} trip={trip} compact />}
              <button type="button" className="tm-layer-close" aria-label="Close" onClick={closeOverReader}>
                <Icon name="close" size={20} />
              </button>
            </div>
          </div>
          <KeptPanel className="tm-layer-body" name={`read-${fromReader.id}`}>
            {readerCard}
          </KeptPanel>
        </section>
      )}
    </>
  );
  // On a phone, the booking form is a sheet over the card it's for, its X back to the card.
  const bookingSheet = !wide && bookingFor && (
    <section key={`booking-${bookingFor.id}`} className="tm-phone-layer" aria-label={`Booking: ${bookingFor.title}`}>
      <div className="tm-layer-bar">
        <div className="tm-layer-actions">
          <button type="button" className="tm-layer-close" aria-label="Close the booking form" onClick={closeBooking}>
            <Icon name="close" size={20} />
          </button>
        </div>
      </div>
      <div className="tm-layer-body">{bookingForm(bookingFor)}</div>
    </section>
  );
  return (
    <PhoneTrip
      trip={trip}
      view={current}
      brief={sel !== null && !offMap(sel.id)}
      name={name("tm-pill-name")}
      setView={setView}
      views={views}
      badges={badges}
      strip={viewBar}
      live={live}
      filters={filters}
      over={
        over || reader ? (
          <>
            {baseOver}
            {readerSheets}
            {bookingSheet}
          </>
        ) : null
      }
      // Above the tabs: on the map, the list button, which becomes "Map" in the same place while the list is
      // open (one toggle), with Today beside it, the two centred together; a tapped place in brief in their
      // place; nothing under a card in full, nor under a finding read, nor while Today or Trip settings shows
      // (the map's List isn't Trip settings'). On the other views, Today alone in that spot.
      bottom={
        reader || settings || over === "today" || over === "today-card" ? null : view !== "map" ? (
          // On Ideas, Days and Bookings, Add beside Today (alone without it), the two centred together, while
          // nothing is open over the view.
          (view === "ideas" || view === "days" || view === "bookings") &&
          !over &&
          sel === null &&
          !trip.locked &&
          !viewing ? (
            <div className="tm-float-row">
              {todayButton}
              {addFor(view, "float")}
            </div>
          ) : (
            todayButton
          )
        ) : over === "list" ? (
          <div className="tm-float-row">
            <button type="button" className="tm-list-button" onClick={closeOver}>
              <Icon name="map" size={20} />
              <span className="callout">Map</span>
            </button>
            {todayButton}
          </div>
        ) : over === "full" ? null : sel ? (
          <Peek
            item={sel}
            trip={trip}
            options={ofChoice?.options ?? []}
            onOpen={() => openOver("full")}
            onClose={() => select(null)}
            ask={peekAsk(sel, () => openOver("full"))}
          />
        ) : (
          <>
            {/* Nothing on the map: it says why, above the list button, where a tapped place shows in brief
                (an empty map has no room to lose). */}
            {placed === 0 && <MapEmpty trip={trip.title} anything={visible.length > 0} />}
            <div className="tm-float-row">
              <button type="button" className="tm-list-button" onClick={() => openOver("list")}>
                <ListIcon />
                <span className="callout">{nearMe === null ? "List" : "Near you"}</span>
                {(nearMe ?? listed.length) > 0 && <span className="callout tm-muted">{nearMe ?? listed.length}</span>}
              </button>
              {todayButton}
            </div>
          </>
        )
      }
      map={(padding, measure, tabs) => (
        <TripMap
          measured={measure}
          tabs={tabs}
          trip={trip.id}
          items={onMap}
          selected={sel?.id ?? null}
          opacity={opacity}
          suggested={suggestedPin}
          option={isOption}
          apart={isApart}
          myYes={myYes}
          frame={frame}
          searchFrame={searchFrame}
          searched={searched}
          onSelect={pickStable}
          theme={theme}
          tiles={tiles}
          glyphs={me.map.glyphs}
          padding={padding}
          me={where.fix}
          others={others}
          corner={locate}
        />
      )}
      other={<LastOpened value={marked}>{other}</LastOpened>}
    />
  );
}

// At the map's foot on a computer, under the notices: the selected place's small card, in brief, and in its
// place while the pointer is on it, the small card of a place the pointer rests on (its row or its pin;
// hover.ts `useHoverCard`), which a click opens. Drawn apart from the page, so a hover never draws the list
// or the map again. The selected one's stays laid out under it, so nothing measured moves.
function FootCards({
  hover,
  brief,
  briefRef,
  selected,
  resting,
}: {
  hover: Hovered;
  brief: ReactNode;
  briefRef: (el: HTMLElement | null) => void;
  selected: string | null;
  resting: (id: string) => ReactNode;
}) {
  const at = useHoverCard(hover);
  const card = at !== null && at !== selected ? resting(at) : null;
  if (!brief && !card) return null;
  return (
    <div className="tm-map-cards">
      {brief && (
        <div className={`tm-map-peek${card ? " is-covered" : ""}`} ref={briefRef}>
          {brief}
        </div>
      )}
      {card && at && (
        <div
          className="tm-map-resting"
          // The pointer on the card keeps it, and its pin and row lit, as on them.
          onPointerEnter={(e) => hover.hovers(e.nativeEvent) && hover.set(at, "card")}
          onPointerLeave={() => hover.leave("card")}
        >
          {card}
        </div>
      )}
    </div>
  );
}

function PhoneTrip(props: {
  trip: TripView;
  // The view shown; none while Trip settings is open (`other` is it then).
  view: View | null;
  // Whether a place is selected: its small card shows at the map's foot once nothing's over the map.
  brief: boolean;
  // The trip's name, which opens Trip settings.
  name: ReactNode;
  setView: (v: View) => void;
  // The views it has tabs for (a shared trip's view has fewer).
  views: typeof VIEWS;
  badges: Partial<Record<View, number>>;
  // A shared trip's strip (ViewBar.tsx), across the top of the screen above everything else, never over the map.
  strip: ReactNode;
  live: ReactNode;
  filters: ReactNode;
  // The list or a card in full, over the map, or a card in full over another view; else what floats above
  // the tabs (on the map the list button and Today, or the tapped place's small card; Today on the others).
  over: ReactNode;
  bottom: ReactNode;
  // The map, given the room kept clear of what floats over it, that room as laid out now, and the tab bar's
  // height over the map's foot.
  map: (padding: Padding, measure: () => Padding, tabs: number) => ReactNode;
  other: ReactNode;
}) {
  const { trip, view, setView, badges } = props;
  const briefRef = useRef(props.brief);
  briefRef.current = props.brief;
  // The map keeps its pins clear of the top bar and of what floats above the tabs, whatever their height;
  // the other views end where the tab bar begins and their lists end clear of what floats over them
  // (DESIGN.md, "A view's end clears the tabs"): the tab bar is as tall as the phone's bottom inset makes it
  // (a home-indicator strip adds 34 px on most iPhones), so it's measured, never assumed. Until then (the
  // first layout) the CSS's own estimate stands (`.tm-trip-page.is-phone`).
  const top = useRef<HTMLDivElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const [covered, setCovered] = useState({ top: 124, bottom: 140, notices: 0, tabs: null as number | null });
  // The map as laid out with nothing over it (the filters over its top, the list button at its foot), and
  // its foot with a place's small card in it, for what moves the map under the list or a card in full.
  const bare = useRef({ top: 124, bottom: 140, brief: 0 });
  const overRef = useRef(props.over);
  overRef.current = props.over;
  // Measured before the page paints, so the map's opening frame already keeps clear of them.
  useLayoutEffect(() => {
    const measure = () => {
      const [t, b] = [top.current?.offsetHeight ?? 124, bottom.current?.offsetHeight ?? 140];
      setCovered({
        top: t,
        bottom: b,
        notices: lasting(top.current?.querySelector<HTMLElement>(".tm-notices") ?? null),
        tabs: bottom.current?.querySelector<HTMLElement>(".tm-tabbar")?.offsetHeight ?? null,
      });
      if (overRef.current) return;
      if (bottom.current?.querySelector(".tm-peek")) bare.current = { ...bare.current, top: t, brief: b };
      else bare.current = { ...bare.current, top: t, bottom: b };
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (top.current) observer.observe(top.current);
    if (bottom.current) observer.observe(bottom.current);
    return () => observer.disconnect();
  }, []);
  const padding: Padding = { top: covered.top + 16, bottom: covered.bottom + 16, left: 16, right: 16 };
  // And as laid out the moment the map moves (TripMap's `measured`), or, under the list or a card in full,
  // as it will be once they go: a row tapped in the list opens its card over the map as the map moves to
  // the place (and frames its choice's options), which shows with the filters over the map's top and its
  // small card at the map's foot (found 2026-10-19, when a row closed the list: framed as the screen was
  // with the list open, Mitsui Garden landed under the filters).
  const measure = useCallback((): Padding => {
    if (!overRef.current)
      return {
        top: (top.current?.offsetHeight ?? 124) + 16,
        bottom: (bottom.current?.offsetHeight ?? 140) + 16,
        left: 16,
        right: 16,
      };
    const { top: t, bottom: b, brief } = bare.current;
    const foot = briefRef.current ? brief || b + BRIEF_MORE : b;
    return { top: t + 16, bottom: foot + 16, left: 16, right: 16 };
  }, []);

  return (
    <div
      className={`tm-trip-page is-phone${props.strip ? " has-strip" : ""}`}
      style={
        {
          "--tm-map-top": `${covered.top}px`,
          "--tm-map-bottom": `${covered.bottom}px`,
          "--tm-notices": covered.notices ? `${covered.notices + 10}px` : "0px",
          // The tab bar, and what floats above it (over the other views, Today): their lists end clear of both.
          ...(covered.tabs === null
            ? {}
            : { "--tm-tabs": `${covered.tabs}px`, "--tm-float": `${covered.bottom - covered.tabs}px` }),
        } as CSSProperties
      }
    >
      {props.strip}
      {/* Everything else, under the strip: the map or a view, what floats over it, and what's opened over it. */}
      <div className="tm-phone-stage">
        {view === "map" ? (
          <main className="tm-map-area" aria-label="Map of the trip" inert={Boolean(props.over)}>
            <CaughtView what="the map" view="map">
              {props.map(padding, measure, covered.tabs ?? 0)}
            </CaughtView>
          </main>
        ) : (
          // Another view, under what's opened from it (and kept as it was while that's open).
          <div className="tm-view-main" inert={Boolean(props.over)}>
            <CaughtView what={viewWords(view)} view={view ?? "settings"}>
              {props.other}
            </CaughtView>
          </div>
        )}
        <div className="tm-phone-top" ref={top}>
          <div className="tm-pill">
            {props.name}
            <AvatarStack people={trip.members} size={28} />
          </div>
          {view === "map" && !props.over && <div className="tm-phone-chips">{props.filters}</div>}
          {props.live}
        </div>
        {props.over}
        <div className="tm-phone-bottom" ref={bottom}>
          {props.bottom && (
            <div className={`tm-phone-float${view === "map" ? "" : " is-over-view"}`}>{props.bottom}</div>
          )}
          <nav className="sv-tabbar tm-tabbar" aria-label="Views">
            {props.views.map((v) => (
              <button
                key={v.key}
                type="button"
                className={`sv-tab${v.key === view ? " is-on" : ""}`}
                aria-current={v.key === view ? "page" : undefined}
                onClick={() => setView(v.key)}
              >
                <span className="tm-tab-icon">
                  <Icon name={v.icon} size={22} />
                  <Badge count={badges[v.key] ?? 0} />
                </span>
                <span>{v.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}

// The map's filters: whose wants (everyone, or one person), yes only, one friend's picks, and the kinds
// to show (none means everything). Kept per trip on this device as they change, and put back when the
// trip opens (reopen.ts); what no longer fits the trip (a person who left, a friend with no picks in the
// plan any more, a kind that isn't one) is dropped. "Yes only" was "Musts only", kept as `mustsOnly`, until
// 2026-10-23: one kept so reads as Yes only.
interface Filters {
  person: string;
  yesOnly: boolean;
  friend: string | null;
  kinds: Kind[];
}

function useFilters(user: string, trip: TripView, items: Item[]) {
  const [filters, setFilters] = useState<Filters>(() => {
    const kept = keptFilters(user, trip.id) as Partial<Record<keyof Filters | "mustsOnly", unknown>> | null;
    const { person, yesOnly, mustsOnly, friend, kinds } = typeof kept === "object" && kept !== null ? kept : {};
    const options = openOptions(items);
    const friends = new Set([...inPlan(items), ...items.filter((i) => options.has(i.id))].flatMap(recommenders));
    return {
      person: typeof person === "string" && trip.members.some((m) => m.id === person) ? person : "all",
      yesOnly: (yesOnly ?? mustsOnly) === true,
      friend: typeof friend === "string" && friends.has(friend) ? friend : null,
      kinds: Array.isArray(kinds) ? KINDS.map((k) => k.key).filter((k) => kinds.includes(k)) : [],
    };
  });
  useEffect(() => keepFilters(user, trip.id, filters), [user, trip.id, filters]);
  return [filters, setFilters] as const;
}

// The map's list, or what's open, in the panel beside the map or over it on a phone: it scrolls, and
// Back finds each where it was.
// The map's list shows what's selected by its lit row (MapList.tsx), there at once as it shows.
const LIT_ROW: Focus = {
  selector: ".tm-overview .tm-row[aria-current=true]",
  wanted: ".tm-overview[data-selected]",
  under: ".tm-list-search",
};

function KeptPanel(props: { className: string; name: string; label?: string; children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  useKeptScroll(ref, props.name, LIT_ROW);
  return props.label === undefined ? (
    <div className={props.className} ref={ref as RefObject<HTMLDivElement>}>
      {props.children}
    </div>
  ) : (
    <aside className={props.className} aria-label={props.label} ref={ref}>
      {props.children}
    </aside>
  );
}

// How much of the trip's notices stays a while (a new version, no signal, the invitation): the other
// views start below that. A banner that passes (changes heard, Undo after removing a to-do) covers
// them instead, so what I'm reading doesn't move when one comes.
function lasting(notices: HTMLElement | null): number {
  if (!notices) return 0;
  const passing = [...notices.querySelectorAll<HTMLElement>(".tm-live-passing")];
  if (passing.length === 0) return notices.offsetHeight;
  if (passing.length === notices.childElementCount) return 0;
  // Each passing one, and the gap that came with it.
  return Math.max(0, notices.offsetHeight - passing.reduce((sum, el) => sum + el.offsetHeight + 8, 0));
}

// On a phone, a map with nothing on it says why, and what to ask: nothing in the trip yet, or nothing
// with coordinates yet. On a computer the list beside it says so.
function MapEmpty({ trip, anything }: { trip: string; anything: boolean }) {
  const readOnly = useReadOnly();
  return (
    <aside className="tm-map-empty" aria-label={readOnly ? "Nothing on the map" : "Nothing on the map yet"}>
      <Empty
        prompt={anything ? examplePrompt(trip, "mapPlaces") : viewPrompt("map", trip)}
        readOnly={anything ? "Nothing on the map." : "Nothing in the trip."}
      >
        {anything
          ? "Nothing on the map yet. Things show here once your AI knows where they are."
          : "Nothing on the map yet. Places, stays and journeys show here as your AI adds them."}
      </Empty>
    </aside>
  );
}

// Why a trip's page didn't open, when it's that this person isn't in it: there's no such trip for them
// (Tripmo answers the same whether it exists or not), or they asked to join and aren't let in yet.
function notIn(error: Error, item: boolean): { title: string; body: string } | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 404)
    return {
      title: item ? "This is in a trip you're not in" : "This is a trip you're not in",
      body: "Ask whoever sent you the link for the trip's join link. Once you're in, this link opens it.",
    };
  if (error.status === 403)
    return {
      title: "You've asked to join this trip",
      body: "Its owner hasn't let you in yet. Once they do, this link opens it.",
    };
  return null;
}

function NotInTrip({ title, body }: { title: string; body: string }) {
  // The tab is just "Tripmo": nothing of the trip or the link (features/map.md, Share).
  useEffect(() => {
    document.title = "Tripmo";
  }, []);
  return <Message title={title} body={body} action={<BackToTrips />} />;
}

// A link to something that's no longer in the trip (an AI's, a notification's, a place someone shared):
// the trip opens, saying it was deleted, by whom and when, as the trip's history has it; or dropped. OK
// puts it away, and the address no longer names it.
function ItemGone({
  trip,
  id,
  dropped,
  onDone,
}: {
  trip: TripView;
  id: string;
  // Still in the trip, but dropped from it.
  dropped: Item | null;
  onDone: () => void;
}) {
  // A shared trip's view has no history to read: it says only that it isn't there.
  const viewing = useViewing();
  const history = useLoad(
    () => (dropped || viewing ? Promise.resolve(null) : api.changesTo(trip.id, id)),
    [trip.id, id, dropped === null],
  );
  if (history.state === "loading") return null;
  const text = dropped
    ? `Dropped from the trip: ${dropped.title}`
    : history.state === "ready"
      ? deletedText(history.data?.changes ?? [], id, trip.you)
      : null;
  return (
    <div className="tm-live tm-live-passing tm-gone" role="status">
      <span className="callout">{text ?? "That isn't in the trip any more."}</span>
      <Button variant="primary" size="sm" onClick={onDone}>
        OK
      </Button>
    </div>
  );
}

// "Deleted by Mei 2 days ago: Fushimi Inari Taisha", from the changes that touched it, newest first. Who and
// when first: a long name ends with an ellipsis on a phone.
function deletedText(changes: Change[], id: string, you: string): string | null {
  for (const change of changes) {
    const op = change.ops.find((o) => (o.record === "item" || o.record === "research") && o.id === id);
    if (!op) continue;
    if (op.op !== "delete") return null;
    const title = typeof op.before?.["title"] === "string" ? op.before["title"] : "what that link opened";
    const by = "id" in change.by ? (change.by.id === you ? "you" : change.by.name) : null;
    return `Deleted${by ? ` by ${by}` : ""} ${ago(change.at)}: ${title}`;
  }
  return null;
}

// Someone changed the trip while it was open: say who and how much, and load it when asked. A newer
// change makes a new banner (the key), so Refresh is offered again.
function LiveBanner({ text, onRefresh }: { text: string; onRefresh: () => void }) {
  const [loading, setLoading] = useState(false);
  return (
    <div className="tm-live tm-live-passing" role="status">
      <span className="callout">{text}</span>
      <Button
        variant="primary"
        size="sm"
        disabled={loading}
        onClick={() => {
          setLoading(true);
          onRefresh();
        }}
      >
        {loading ? "Refreshing…" : "Refresh"}
      </Button>
    </div>
  );
}

// Three lines, drawn like Souvenir's icons (it has no list icon).
function ListIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />
    </svg>
  );
}
